/**
 * UnifiedAIChat - AI assistant with RAG querying AND action execution
 *
 * Features:
 * - Context-aware (knows about current board/project/meeting)
 * - Executes actions (create/update/delete tasks, boards, etc.)
 * - Action preview and confirmation
 * - Multi-turn conversations with persistent history
 * - Streaming responses
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Mic, Volume2, Square } from 'lucide-react';
import { chatApi, type ConversationAction, type ConversationMessage } from '@services/api/chat';
import { searchApi, type UnifiedSearchResult } from '@services/api/search';
import { useVoiceStore } from '@stores/voiceStore';
import { useTextToSpeech } from '@services/voice/textToSpeech';
import { useSpeechToText } from '@services/voice/speechToText';
import ActionPreview from './ActionPreview';
import SearchResultCard from './SearchResultCard';

export interface UnifiedAIChatContext {
  type: 'board' | 'project' | 'meeting' | 'global';
  boardId?: string;
  projectId?: string;
  meetingId?: string;
  contextLabel?: string;
}

export interface UnifiedAIChatProps {
  context: UnifiedAIChatContext;
  conversationId?: string | undefined; // Optional: resume existing conversation
  onClose: () => void;
  onTaskClick?: ((taskId: string, boardId: string) => void) | undefined;
  onBoardClick?: ((boardId: string) => void) | undefined;
  onProjectClick?: ((projectId: string) => void) | undefined;
  onMeetingClick?: ((meetingId: string) => void) | undefined;
}

interface Message extends ConversationMessage {
  isStreaming?: boolean;
  actions?: ConversationAction[];
  searchResults?: UnifiedSearchResult[];
}

export default function UnifiedAIChat({
  context,
  conversationId: initialConversationId,
  onClose,
  onTaskClick,
  onBoardClick,
  onProjectClick,
  onMeetingClick,
}: UnifiedAIChatProps) {
  const { t: _t, i18n } = useTranslation();
  const [conversationId, setConversationId] = useState<string | undefined>(initialConversationId);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [pendingActions, setPendingActions] = useState<Map<string, ConversationAction>>(new Map());
  const [lastSpokenMessageId, setLastSpokenMessageId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Voice hooks
  const { preferences } = useVoiceStore();
  const { speak } = useTextToSpeech();
  const speakRef = useRef(speak);

  // Keep speakRef updated without triggering effects
  useEffect(() => {
    speakRef.current = speak;
  }, [speak]);

  // Load existing conversation if conversationId provided
  useEffect(() => {
    const loadConversation = async () => {
      if (initialConversationId) {
        try {
          const conversation = await chatApi.getConversation(initialConversationId);
          setMessages(conversation.messages as Message[]);

          // Track pending actions
          const pending = new Map<string, ConversationAction>();
          conversation.actions
            .filter(a => a.status === 'pending')
            .forEach(a => pending.set(a.id, a));
          setPendingActions(pending);
        } catch (err) {
          console.error('Failed to load conversation:', err);
          setError(err instanceof Error ? err.message : _t('chat.errors.failedToLoadConversation', 'Failed to load conversation'));
        }
      }
    };
    void loadConversation();
  }, [initialConversationId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-play TTS for new assistant messages
  useEffect(() => {
    if (!preferences.autoPlay) return;

    const lastMsg = messages[messages.length - 1];
    if (
      lastMsg?.role === 'assistant' &&
      !lastMsg.isStreaming &&
      lastMsg.content &&
      lastMsg.id !== lastSpokenMessageId
    ) {
      void speakRef.current(lastMsg.content);
      setLastSpokenMessageId(lastMsg.id);
    }
  }, [messages, preferences.autoPlay, lastSpokenMessageId]);

  const handleDirectSearch = async (query: string) => {
    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId || '',
      role: 'user',
      content: `/search ${query}`,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const contextId = context.boardId || context.projectId || context.meetingId;
      const response = await searchApi.unifiedSearch(query, {
        context_type: context.type,
        ...(contextId ? { context_id: contextId } : {}),
        limit: 10,
      });

      const resultMsg: Message = {
        id: `search-${Date.now()}`,
        conversation_id: conversationId || '',
        role: 'assistant',
        content: response.total_count > 0
          ? _t('search.foundResults', 'Found {{count}} results in {{time}}ms', {
              count: response.total_count,
              time: Math.round(response.took_ms),
            })
          : _t('search.noResults', 'No results found for "{{query}}"', { query }),
        created_at: new Date().toISOString(),
        searchResults: response.results,
      };
      setMessages(prev => [...prev, resultMsg]);
    } catch (err) {
      console.error('Search error:', err);
      setError(
        err instanceof Error
          ? err.message
          : _t('search.error', 'Search failed'),
      );
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userContent = input.trim();
    setInput('');
    setError(null);

    // Handle /search command directly
    if (userContent.startsWith('/search ')) {
      const searchQuery = userContent.slice(8).trim();
      if (searchQuery) {
        setIsLoading(true);
        try {
          await handleDirectSearch(searchQuery);
        } finally {
          setIsLoading(false);
        }
        return;
      }
    }

    setIsLoading(true);

    // Add user message immediately (optimistic)
    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId || '',
      role: 'user',
      content: userContent,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Create assistant message placeholder for streaming
    const assistantMessageId = `temp-assistant-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      {
        id: assistantMessageId,
        conversation_id: conversationId || '',
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        isStreaming: true,
      },
    ]);

    try {
      // Send message and get full response with actions
      const responseData = await chatApi.sendMessage({
        message: userContent,
        conversation_id: conversationId,
        context_type: context.type,
        context_id: context.boardId || context.projectId || context.meetingId,
        stream: false,
        user_locale: i18n.language,
      });

      // Update streaming placeholder with actual content
      setMessages(prev =>
        prev.map(msg =>
          msg.id === assistantMessageId
            ? { ...msg, content: responseData.content || '', isStreaming: false }
            : msg
        )
      );

      // Update conversation ID if this was first message
      if (!conversationId) {
        setConversationId(responseData.conversation_id);
      }

      // Extract search results from action_results metadata if present
      let searchResults: UnifiedSearchResult[] | undefined;
      if (responseData.actions) {
        for (const action of responseData.actions) {
          if (
            action.action_type === 'search_knowledge' &&
            action.result?.results
          ) {
            searchResults = action.result.results as UnifiedSearchResult[];
          }
        }
      }

      // Update messages with real IDs and actions
      setMessages(prev => {
        const updated = prev.filter(msg => msg.id !== userMessage.id && msg.id !== assistantMessageId);
        return [
          ...updated,
          {
            id: responseData.message_id,
            conversation_id: responseData.conversation_id,
            role: 'user',
            content: userContent,
            created_at: new Date().toISOString(),
          },
          {
            id: responseData.assistant_message_id,
            conversation_id: responseData.conversation_id,
            role: 'assistant' as const,
            content: responseData.content || '',
            created_at: new Date().toISOString(),
            isStreaming: false,
            actions: responseData.actions,
            ...(searchResults ? { searchResults } : {}),
          },
        ];
      });

      // Track pending actions
      const pending = new Map(pendingActions);
      responseData.actions
        .filter((a: ConversationAction) => a.status === 'pending')
        .forEach((a: ConversationAction) => pending.set(a.id, a));
      setPendingActions(pending);

    } catch (err) {
      console.error('Chat error:', err);
      setError(err instanceof Error ? err.message : _t('chat.errors.failedToSendMessage', 'Failed to send message'));
      // Remove incomplete assistant message
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmAction = async (actionId: string, confirmed: boolean, modifications?: Record<string, unknown>) => {
    try {
      const result = await chatApi.confirmAction(actionId, { confirmed, modifications });

      // Remove from pending
      const updated = new Map(pendingActions);
      updated.delete(actionId);
      setPendingActions(updated);

      // Show success message
      if (confirmed) {
        setMessages(prev => [
          ...prev,
          {
            id: `system-${Date.now()}`,
            conversation_id: conversationId || '',
            role: 'system',
            content: `✓ ${result.message}`,
            created_at: new Date().toISOString(),
          },
        ]);
      }
    } catch (err) {
      console.error('Action confirmation failed:', err);
      setError(err instanceof Error ? err.message : _t('chat.errors.actionConfirmationFailed', 'Action confirmation failed'));
    }
  };

  const handleUndoAction = async (actionId: string) => {
    try {
      const result = await chatApi.undoAction(actionId);
      setMessages(prev => [
        ...prev,
        {
          id: `system-${Date.now()}`,
          conversation_id: conversationId || '',
          role: 'system',
          content: `↺ ${result.message}`,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      console.error('Undo failed:', err);
      setError(err instanceof Error ? err.message : _t('chat.errors.undoFailed', 'Undo failed'));
    }
  };

  const handleClear = async () => {
    if (conversationId) {
      try {
        await chatApi.deleteConversation(conversationId);
      } catch (err) {
        console.error('Failed to delete conversation:', err);
        setError(err instanceof Error ? err.message : _t('chat.errors.clearFailed', 'Failed to delete conversation'));
        return;
      }
    }
    setMessages([]);
    setConversationId(undefined);
    setPendingActions(new Map());
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    // Only adjust height if target has style property (not for synthetic events)
    if (e.target.style) {
      e.target.style.height = 'auto';
      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
    }
  };

  const getContextBadge = () => {
    if (context.contextLabel) return context.contextLabel;
    switch (context.type) {
      case 'board': return 'Board Context';
      case 'project': return 'Project Context';
      case 'meeting': return 'Meeting Context';
      case 'global': return 'Global';
      default: return '';
    }
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-all"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span className="text-sm font-medium">AI Assistant</span>
          {messages.length > 0 && (
            <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
              {messages.length}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Mobile: Full screen overlay */}
      <div className="fixed inset-0 z-50 sm:hidden bg-white dark:bg-neutral-900 flex flex-col">
        <ChatHeader
          onClose={onClose}
          onMinimize={() => setIsMinimized(true)}
          onClear={handleClear}
          hasMessages={messages.length > 0}
          contextBadge={getContextBadge()}
        />

        <ChatMessages
          messages={messages}
          error={error}
          messagesEndRef={messagesEndRef}
          pendingActions={pendingActions}
          onConfirmAction={handleConfirmAction}
          onUndoAction={handleUndoAction}
          onTaskClick={onTaskClick}
          onBoardClick={onBoardClick}
          onProjectClick={onProjectClick}
          onMeetingClick={onMeetingClick}
        />

        <ChatInput
          input={input}
          isLoading={isLoading}
          inputRef={inputRef}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onSubmit={handleSubmit}
        />
      </div>

      {/* Desktop: Floating window */}
      <div className="fixed bottom-4 right-4 z-50 hidden sm:flex flex-col w-[400px] h-[500px] bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <ChatHeader
          onClose={onClose}
          onMinimize={() => setIsMinimized(true)}
          onClear={handleClear}
          hasMessages={messages.length > 0}
          contextBadge={getContextBadge()}
          compact
        />

        <ChatMessages
          messages={messages}
          error={error}
          messagesEndRef={messagesEndRef}
          pendingActions={pendingActions}
          onConfirmAction={handleConfirmAction}
          onUndoAction={handleUndoAction}
          onTaskClick={onTaskClick}
          onBoardClick={onBoardClick}
          onProjectClick={onProjectClick}
          onMeetingClick={onMeetingClick}
          compact
        />

        <ChatInput
          input={input}
          isLoading={isLoading}
          inputRef={inputRef}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onSubmit={handleSubmit}
          compact
        />
      </div>
    </>
  );
}

// ===================================================================
// Sub-components
// ===================================================================

interface ChatHeaderProps {
  onClose: () => void;
  onMinimize: () => void;
  onClear: () => void;
  hasMessages: boolean;
  contextBadge: string;
  compact?: boolean;
}

function ChatHeader({ onClose, onMinimize, onClear, hasMessages, contextBadge, compact = false }: ChatHeaderProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className={`flex items-center justify-between ${compact ? 'px-4 py-3' : 'px-4 py-3'} border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800`}>
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span className={`font-medium text-neutral-900 dark:text-white ${compact ? 'text-sm' : ''}`}>
            {t('chat.aiAssistant', 'AI Assistant')}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {hasMessages && (
            <button
              onClick={onClear}
              className="p-1.5 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded"
              title={t('chat.clearConversation', 'Clear conversation')}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
          <button
            onClick={onMinimize}
            className="p-1.5 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded"
            title={t('common.minimize')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded"
            title={t('common.close')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Context badge */}
      <div className="px-4 py-1.5 bg-neutral-100 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
        <span className="text-xs text-neutral-500 dark:text-neutral-400">{contextBadge}</span>
      </div>
    </>
  );
}

interface ChatMessagesProps {
  messages: Message[];
  error: string | null;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  pendingActions: Map<string, ConversationAction>;
  onConfirmAction: (actionId: string, confirmed: boolean, modifications?: Record<string, unknown> | undefined) => void;
  onUndoAction: (actionId: string) => void;
  onTaskClick?: ((taskId: string, boardId: string) => void) | undefined;
  onBoardClick?: ((boardId: string) => void) | undefined;
  onProjectClick?: ((projectId: string) => void) | undefined;
  onMeetingClick?: ((meetingId: string) => void) | undefined;
  compact?: boolean;
}

function ChatMessages({
  messages,
  error,
  messagesEndRef,
  pendingActions,
  onConfirmAction,
  onUndoAction,
  onTaskClick,
  onBoardClick,
  onProjectClick,
  onMeetingClick,
  compact = false,
}: ChatMessagesProps) {
  const { t: _t } = useTranslation();

  return (
    <div className={`flex-1 overflow-y-auto ${compact ? 'p-3 space-y-3' : 'p-4 space-y-4'}`}>
      {messages.length === 0 && (
        <div className="text-center text-neutral-500 dark:text-neutral-400 py-8">
          <svg className={`${compact ? 'w-10 h-10' : 'w-12 h-12'} mx-auto mb-3 text-neutral-300 dark:text-neutral-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <p className="text-sm">{_t('chat.askAnything', 'Ask me anything!')}</p>
          <p className="text-xs mt-1">{_t('chat.capabilities', 'I can search, create tasks, update boards, and more. Try /search to search directly.')}</p>
        </div>
      )}
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          pendingActions={pendingActions}
          onConfirmAction={onConfirmAction}
          onUndoAction={onUndoAction}
          onTaskClick={onTaskClick}
          onBoardClick={onBoardClick}
          onProjectClick={onProjectClick}
          onMeetingClick={onMeetingClick}
          compact={compact}
        />
      ))}
      {error && (
        <div className={`${compact ? 'p-2 text-xs' : 'p-3 text-sm'} bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400`}>
          {error}
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}

interface ChatInputProps {
  input: string;
  isLoading: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e?: React.FormEvent | undefined) => void;
  compact?: boolean | undefined;
}

function ChatInput({ input, isLoading, inputRef, onInputChange, onKeyDown, onSubmit, compact = false }: ChatInputProps) {
  const { t } = useTranslation();
  const { isListening, transcript, startListening, stopListening, isSupported, error } = useSpeechToText();
  const { preferences } = useVoiceStore();
  const lastProcessedTranscriptRef = useRef<string>('');
  const isSpaceKeyDownRef = useRef(false);

  // Reset processed transcript tracker when starting a new recording
  useEffect(() => {
    if (isListening) {
      lastProcessedTranscriptRef.current = '';
    }
  }, [isListening]);

  // Handle voice input transcript
  useEffect(() => {
    if (transcript.isFinal && transcript.text && transcript.text !== lastProcessedTranscriptRef.current) {
      // Mark this transcript as processed
      lastProcessedTranscriptRef.current = transcript.text;

      // Update input with transcript
      const event = {
        target: {
          value: input ? `${input} ${transcript.text}` : transcript.text,
        },
      } as React.ChangeEvent<HTMLTextAreaElement>;
      onInputChange(event);
    }
  }, [transcript, input, onInputChange]);

  // Handle push-to-talk mode with spacebar
  useEffect(() => {
    if (!preferences.pushToTalkMode || !isSupported || isLoading) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only activate on spacebar when textarea is focused AND input is empty
      // This prevents interfering with normal typing (spaces between words)
      if (
        e.code === 'Space' &&
        !isSpaceKeyDownRef.current &&
        document.activeElement === inputRef.current &&
        !input.trim() // Only activate when input is empty
      ) {
        // Prevent space from being typed
        e.preventDefault();
        isSpaceKeyDownRef.current = true;
        void startListening();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && isSpaceKeyDownRef.current) {
        isSpaceKeyDownRef.current = false;
        stopListening();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      isSpaceKeyDownRef.current = false;
    };
  }, [preferences.pushToTalkMode, isSupported, isLoading, startListening, stopListening, inputRef, input]);

  const handleVoiceToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      void startListening();
    }
  };

  // Get placeholder text based on state
  const getPlaceholder = () => {
    if (isListening) {
      return t('voice.listening', 'Listening...');
    }
    if (preferences.pushToTalkMode && isSupported && !input.trim()) {
      return t('voice.pushToTalkHint', 'Hold spacebar to speak or type your message');
    }
    return t('common.askAnything', 'Ask me anything...');
  };

  return (
    <div className={`${compact ? 'p-3' : 'p-4'} border-t border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900`}>
      <form onSubmit={onSubmit} className="flex gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          placeholder={getPlaceholder()}
          className={`flex-1 px-3 py-2 ${compact ? 'text-sm' : ''} border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-500 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500`}
          rows={1}
          disabled={isLoading}
        />

        {/* Voice Input Button */}
        {isSupported && (
          <button
            type="button"
            onClick={handleVoiceToggle}
            disabled={isLoading}
            className={`${compact ? 'px-3 py-2' : 'px-3 py-2'} ${
              isListening
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600'
            } text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all`}
            title={isListening ? t('voice.stopListening', 'Stop listening') : t('voice.startListening', 'Start voice input')}
          >
            <Mic className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${isListening ? 'animate-pulse' : ''}`} />
          </button>
        )}

        {/* Send Button */}
        <button
          type="submit"
          disabled={!input.trim() || isLoading}
          className={`${compact ? 'px-3 py-2' : 'px-4 py-2'} bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all`}
        >
          {isLoading ? (
            <svg className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} animate-spin`} fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </form>

      {/* Voice input error */}
      {error && (
        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-xs text-red-600 dark:text-red-400">
            {error}
          </p>
        </div>
      )}
    </div>
  );
}

interface MessageBubbleProps {
  message: Message;
  pendingActions: Map<string, ConversationAction>;
  onConfirmAction: (actionId: string, confirmed: boolean, modifications?: Record<string, unknown> | undefined) => void;
  onUndoAction: (actionId: string) => void;
  onTaskClick?: ((taskId: string, boardId: string) => void) | undefined;
  onBoardClick?: ((boardId: string) => void) | undefined;
  onProjectClick?: ((projectId: string) => void) | undefined;
  onMeetingClick?: ((meetingId: string) => void) | undefined;
  compact?: boolean | undefined;
}

function MessageBubble({
  message,
  pendingActions,
  onConfirmAction,
  onUndoAction,
  onTaskClick,
  onBoardClick,
  onProjectClick,
  onMeetingClick,
  compact = false,
}: MessageBubbleProps) {
  // Translation hook - must be called before any conditional returns
  const { t } = useTranslation();

  // TTS controls for assistant messages - must be called before any conditional returns
  const { isSpeaking, speak, stop: stopSpeaking } = useTextToSpeech();
  const [isThisMessageSpeaking, setIsThisMessageSpeaking] = useState(false);

  const handleSpeakToggle = () => {
    if (isSpeaking && isThisMessageSpeaking) {
      stopSpeaking();
      setIsThisMessageSpeaking(false);
    } else {
      void speak(message.content);
      setIsThisMessageSpeaking(true);
    }
  };

  // Track when speaking stops
  useEffect(() => {
    if (!isSpeaking && isThisMessageSpeaking) {
      setIsThisMessageSpeaking(false);
    }
  }, [isSpeaking, isThisMessageSpeaking]);

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className={`max-w-[80%] px-3 py-2 bg-primary-600 text-white rounded-lg rounded-br-sm ${compact ? 'text-sm' : ''}`}>
          {message.content}
        </div>
      </div>
    );
  }

  if (message.role === 'system') {
    return (
      <div className="flex justify-center">
        <div className={`px-3 py-1 bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded-full ${compact ? 'text-xs' : 'text-sm'}`}>
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-2">
        <div className={`max-w-[85%] px-3 py-2 bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-white rounded-lg rounded-bl-sm ${compact ? 'text-sm' : ''}`}>
          {message.content ? (
            <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.content}
              </ReactMarkdown>
            </div>
          ) : (
            <span className="text-neutral-400">Thinking...</span>
          )}
          {message.isStreaming && (
            <span className="inline-block w-1.5 h-4 ml-1 bg-primary-500 animate-pulse" />
          )}
        </div>

        {/* TTS Control Button */}
        {!message.isStreaming && message.content && (
          <button
            onClick={handleSpeakToggle}
            className="p-1.5 text-neutral-500 hover:text-primary-600 dark:hover:text-primary-400 rounded transition-colors"
            title={isSpeaking && isThisMessageSpeaking ? t('chat.stopSpeaking', 'Stop speaking') : t('chat.readAloud', 'Read aloud')}
          >
            {isSpeaking && isThisMessageSpeaking ? (
              <Square className={`${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
            ) : (
              <Volume2 className={`${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
            )}
          </button>
        )}
      </div>

      {/* Speaking Indicator */}
      {isSpeaking && isThisMessageSpeaking && (
        <div className="flex items-center gap-2 text-xs text-primary-600 dark:text-primary-400">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
          </span>
          Speaking...
        </div>
      )}

      {/* Search results */}
      {message.searchResults && message.searchResults.length > 0 && (
        <div className="max-w-[85%] space-y-1.5">
          {message.searchResults.slice(0, 5).map((result, idx) => (
            <SearchResultCard
              key={`${result.entity_id}-${idx}`}
              result={result}
              {...(onTaskClick ? { onTaskClick } : {})}
              {...(onBoardClick ? { onBoardClick } : {})}
              {...(onProjectClick ? { onProjectClick } : {})}
              {...(onMeetingClick ? { onMeetingClick } : {})}
              compact={compact}
            />
          ))}
        </div>
      )}

      {/* Action previews and confirmations */}
      {message.actions && message.actions.length > 0 && (
        <div className="max-w-[85%] space-y-2">
          {message.actions.map((action) => (
            <ActionPreview
              key={action.id}
              action={action}
              isPending={pendingActions.has(action.id)}
              onConfirm={(confirmed, modifications) => onConfirmAction(action.id, confirmed, modifications)}
              onUndo={() => onUndoAction(action.id)}
              onTaskClick={onTaskClick}
              onBoardClick={onBoardClick}
              onProjectClick={onProjectClick}
              onMeetingClick={onMeetingClick}
              compact={compact}
            />
          ))}
        </div>
      )}
    </div>
  );
}
